import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Link2, Copy, Check, Info, Save, Download } from 'lucide-react'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { linkTrackerApi } from '../api'
import type { URLAcortadaCreateData, URLAcortadaDetalle } from '../types'
import { CANALES, GRUPOS } from '../types'

export function CrearURL() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [showInfo, setShowInfo] = useState(false)
  const [showUrlInfo, setShowUrlInfo] = useState(false)
  const [showTituloInfo, setShowTituloInfo] = useState(false)
  const [showExpiraInfo, setShowExpiraInfo] = useState(false)
  const [urlCreada, setUrlCreada] = useState<URLAcortadaDetalle | null>(null)
  const [copied, setCopied] = useState<'corta' | 'tracking' | null>(null)
  const [qrUrl, setQrUrl] = useState<string | null>(null)
  const [formData, setFormData] = useState<URLAcortadaCreateData>({
    url_destino: '',
    titulo: '',
    campana: '',
    canal: 'whatsapp',
    grupo: 'tonal',
    expira_dias: undefined,
    habilitar_qr: true,
  })

  useEffect(() => {
    if (!urlCreada || !urlCreada.habilitar_qr) {
      setQrUrl(null)
      return
    }
    let revoked = false
    linkTrackerApi.obtenerQRBlob(urlCreada.codigo).then((blob) => {
      if (!revoked) {
        setQrUrl(URL.createObjectURL(blob))
      }
    }).catch(() => {})
    return () => {
      revoked = true
      if (qrUrl) URL.revokeObjectURL(qrUrl)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlCreada?.codigo])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.url_destino) {
      toast.error('La URL de destino es requerida')
      return
    }

    // Validar URL
    try {
      new URL(formData.url_destino)
    } catch {
      toast.error('La URL de destino no es válida')
      return
    }

    setLoading(true)
    try {
      const result = await linkTrackerApi.crearURL(formData)
      setUrlCreada(result.url)
      toast.success('URL creada exitosamente')
    } catch (error: any) {
      console.error('Error:', error)
      toast.error(error.response?.data?.error || 'Error al crear URL')
    } finally {
      setLoading(false)
    }
  }

  const handleCopy = async (url: string, type: 'corta' | 'tracking') => {
    const success = await linkTrackerApi.copiarAlPortapapeles(url)
    if (success) {
      setCopied(type)
      setTimeout(() => setCopied(null), 2000)
      toast.success('URL copiada al portapapeles')
    } else {
      toast.error('Error al copiar')
    }
  }

  const handleReset = () => {
    setUrlCreada(null)
    setFormData({
      url_destino: '',
      titulo: '',
      campana: '',
      canal: 'whatsapp',
      grupo: 'tonal',
      expira_dias: undefined,
      habilitar_qr: true,
    })
  }

  // Vista de URL creada
  if (urlCreada) {
    return (
      <div className="space-y-6">
        <Card className="border border-gray-300 dark:border-gray-700 border-l-4 border-l-teal-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Check className="h-5 w-5 text-teal-500" />
              URL Creada Exitosamente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {urlCreada.titulo && (
              <div>
                <Label className="text-sm text-muted-foreground">Título</Label>
                <p className="font-medium">{urlCreada.titulo}</p>
              </div>
            )}

            <div>
              <Label className="text-sm text-muted-foreground">URL Corta</Label>
              <div className="flex gap-2 mt-1">
                <Input value={urlCreada.url_corta} readOnly className="font-mono" />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleCopy(urlCreada.url_corta, 'corta')}
                >
                  {copied === 'corta' ? (
                    <Check className="h-4 w-4 text-teal-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-sm text-muted-foreground">
                URL con Tracking (para WhatsApp/Twilio)
              </Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={urlCreada.url_con_tracking}
                  readOnly
                  className="font-mono text-xs"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleCopy(urlCreada.url_con_tracking, 'tracking')}
                >
                  {copied === 'tracking' ? (
                    <Check className="h-4 w-4 text-teal-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Usa esta URL en plantillas de Twilio. El {"{{1}}"} se reemplazará por el número del destinatario.
              </p>
            </div>

            {/* QR Code */}
            {urlCreada.habilitar_qr && <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
              <Label className="text-sm text-muted-foreground">Código QR</Label>
              <div className="flex items-center gap-4">
                <div className="bg-white p-2 rounded-lg border border-gray-200">
                  {qrUrl ? (
                    <img src={qrUrl} alt="QR Code" className="w-48 h-48" />
                  ) : (
                    <div className="w-48 h-48 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Escanéalo para redirigir a la URL de destino.
                  </p>
                  <Button
                    variant="outline"
                    className="border-gray-700 text-gray-700 hover:bg-gray-50 dark:border-gray-500 dark:text-gray-300 dark:hover:bg-gray-800"
                    onClick={() => linkTrackerApi.descargarQR(urlCreada.codigo)}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Descargar QR
                  </Button>
                </div>
              </div>
            </div>}

            <div className="flex gap-2 pt-4">
              <Button className="bg-teal-800 hover:bg-teal-900 text-white" onClick={() => navigate('/link-tracker/urls')}>
                Ver todas las URLs
              </Button>
              <Button variant="outline" className="border-gray-700 text-gray-700 hover:bg-gray-50 dark:border-gray-500 dark:text-gray-300 dark:hover:bg-gray-800" onClick={handleReset}>
                Crear otra URL
              </Button>
              <Button
                variant="outline"
                className="border-gray-700 text-gray-700 hover:bg-gray-50 dark:border-gray-500 dark:text-gray-300 dark:hover:bg-gray-800"
                onClick={() => navigate(`/link-tracker/analytics/${urlCreada.codigo}`)}
              >
                Ver Analytics
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Vista de formulario
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Link2 className="h-6 w-6" />
            Crear URL Acortada
          </h1>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="text-muted-foreground hover:text-foreground transition-colors ml-2"
          >
            <Info className="h-5 w-5" />
          </button>
          {showInfo && (
            <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
              Crea una URL corta con tracking de clics
            </span>
          )}
        </div>
        <Button
          type="submit"
          form="crear-url-form"
          size="icon"
          className="bg-teal-800 hover:bg-teal-900 text-white"
          disabled={loading}
        >
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
          ) : (
            <Save className="h-4 w-4" />
          )}
        </Button>
      </div>

      <Tabs
        value={formData.grupo || 'tonal'}
        onValueChange={(value) => setFormData({ ...formData, grupo: value })}
      >
        <TabsList className="w-full">
          {GRUPOS.map((grupo) => (
            <TabsTrigger key={grupo.value} value={grupo.value} className="flex-1">
              {grupo.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={formData.grupo || 'tonal'} className="space-y-6 mt-4">
          <Card className="border border-gray-300 dark:border-gray-700">
            <CardContent className="pt-6">
              <form id="crear-url-form" onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center">
                    <Label htmlFor="url_destino">URL de Destino *</Label>
                    <button
                      type="button"
                      onClick={() => setShowUrlInfo(!showUrlInfo)}
                      className="text-muted-foreground hover:text-foreground transition-colors ml-1"
                    >
                      <Info className="h-4 w-4" />
                    </button>
                    {showUrlInfo && (
                      <span className="text-muted-foreground ml-2 text-xs animate-in slide-in-from-left-2 duration-200">
                        La URL completa a la que se redirigirá
                      </span>
                    )}
                  </div>
                  <Input
                    id="url_destino"
                    type="url"
                    placeholder="https://ejemplo.com/pagina"
                    value={formData.url_destino}
                    onChange={(e) => setFormData({ ...formData, url_destino: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center">
                    <Label htmlFor="titulo">Título (opcional)</Label>
                    <button
                      type="button"
                      onClick={() => setShowTituloInfo(!showTituloInfo)}
                      className="text-muted-foreground hover:text-foreground transition-colors ml-1"
                    >
                      <Info className="h-4 w-4" />
                    </button>
                    {showTituloInfo && (
                      <span className="text-muted-foreground ml-2 text-xs animate-in slide-in-from-left-2 duration-200">
                        Un nombre descriptivo para identificar esta URL
                      </span>
                    )}
                  </div>
                  <Input
                    id="titulo"
                    placeholder="Promoción Navidad 2025"
                    value={formData.titulo || ''}
                    onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="campana">Campaña (opcional)</Label>
                    <Input
                      id="campana"
                      placeholder="promo_navidad_2025"
                      value={formData.campana || ''}
                      onChange={(e) => setFormData({ ...formData, campana: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="canal">Canal</Label>
                    <Select
                      value={formData.canal}
                      onValueChange={(value) => setFormData({ ...formData, canal: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CANALES.map((canal) => (
                          <SelectItem key={canal.value} value={canal.value}>
                            {canal.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center">
                    <Label htmlFor="expira_dias">Expira en (días, opcional)</Label>
                    <button
                      type="button"
                      onClick={() => setShowExpiraInfo(!showExpiraInfo)}
                      className="text-muted-foreground hover:text-foreground transition-colors ml-1"
                    >
                      <Info className="h-4 w-4" />
                    </button>
                    {showExpiraInfo && (
                      <span className="text-muted-foreground ml-2 text-xs animate-in slide-in-from-left-2 duration-200">
                        Deja vacío para que la URL no expire nunca
                      </span>
                    )}
                  </div>
                  <Input
                    id="expira_dias"
                    type="number"
                    min="1"
                    max="365"
                    placeholder="30"
                    value={formData.expira_dias || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      expira_dias: e.target.value ? parseInt(e.target.value) : undefined
                    })}
                  />
                </div>

                <div className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="habilitar_qr">Habilitar QR</Label>
                    <p className="text-xs text-muted-foreground">
                      Genera un código QR para esta URL
                    </p>
                  </div>
                  <Switch
                    id="habilitar_qr"
                    checked={formData.habilitar_qr || false}
                    onCheckedChange={(checked) => setFormData({ ...formData, habilitar_qr: checked })}
                    className="data-[state=checked]:bg-teal-500"
                  />
                </div>

              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
